// GoldVaultLabel.jsx
// Printable gold vault packaging label with QR code.
// Used after a WalkIn or Pledge case is moved to branch vault.

import React from 'react';
import { Modal, Button, Divider, Space, Typography } from 'antd';
import { PrinterOutlined, CloseOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { renderToStaticMarkup } from 'react-dom/server';
import dayjs from 'dayjs';

const { Text } = Typography;

const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

// Weights printed on the label must come from the Gold Receipt — that is the
// valued, source-of-truth measurement. `caseData.totalGrossWeight` /
// `totalNetWeight` on a pledge case are only the pre-valuation estimate, so they
// are the last fallback (walk-in cases have no receipt and carry final weights).
function resolveWeights(caseData, caseModel, goldReceipt) {
  if (caseModel !== 'WalkInPurchaseCase') {
    const gross = goldReceipt?.finalTotalGrossWeight ?? caseData.finalTotalGrossWeight;
    const net = goldReceipt?.finalTotalNetWeight ?? caseData.finalTotalNetWeight;
    if (gross || net) {
      return {
        gross: gross || caseData.totalGrossWeight || 0,
        net: net || caseData.totalNetWeight || 0,
      };
    }
  }
  return {
    gross: caseData.totalGrossWeight || 0,
    net: caseData.totalNetWeight || 0,
  };
}

function buildQrContent(caseData, caseModel, goldReceipt) {
  const isWalkIn = caseModel === 'WalkInPurchaseCase';
  const weights = resolveWeights(caseData, caseModel, goldReceipt);
  const base = {
    id:     caseData.caseId,
    type:   isWalkIn ? 'WALK_IN' : 'PLEDGE',
    gross:  Number(weights.gross.toFixed(4)),
    net:    Number(weights.net.toFixed(4)),
    cust:   caseData.customerId?.name || '',
    branch: caseData.branch?.name    || '',
    date:   dayjs().format('YYYY-MM-DD'),
  };
  if (isWalkIn) {
    base.purchaseValue = caseData.finalPaymentToCustomer || 0;
  } else {
    base.closureAmt    = caseData.closureAmount || 0;
    base.finalNetValue = caseData.finalNetValue || goldReceipt?.finalNetValue || 0;
  }
  return JSON.stringify(base);
}

function LabelCard({ caseData, caseModel, goldReceipt }) {
  const customer  = caseData.customerId || {};
  const branch    = caseData.branch     || {};
  const isWalkIn  = caseModel === 'WalkInPurchaseCase';
  const weights   = resolveWeights(caseData, caseModel, goldReceipt);
  const gross     = weights.gross.toFixed(4);
  const net       = weights.net.toFixed(4);
  const qrContent = buildQrContent(caseData, caseModel, goldReceipt);
  const typeLabel = isWalkIn ? 'WALK-IN PURCHASE' : 'PLEDGE RELEASE';
  const typeColor = isWalkIn ? '#d46b08' : '#531dab';

  return (
    <div
      style={{
        width: 360,
        border: '2px solid #262626',
        borderRadius: 6,
        background: '#fff',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ background: typeColor, color: '#fff', padding: '5px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>GOLD VAULT LABEL</span>
        <span style={{ fontSize: 11, opacity: 0.9 }}>{typeLabel}</span>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, padding: 10, gap: 10 }}>
        {/* QR */}
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <QRCodeSVG value={qrContent} size={120} bgColor="#ffffff" fgColor="#000000" level="M" />
          <span style={{ fontSize: 9, color: '#595959' }}>Scan to verify</span>
        </div>

        {/* Details */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#262626', lineHeight: 1.1 }}>{caseData.caseId}</div>
          <div style={{ fontSize: 11, color: '#595959', marginBottom: 5 }}>{dayjs().format('DD MMM YYYY, hh:mm A')}</div>
          <Divider style={{ margin: '4px 0', borderColor: '#d9d9d9' }} />

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <tbody>
              <tr>
                <td style={{ color: '#8c8c8c', paddingBottom: 2 }}>Gross Weight</td>
                <td style={{ fontWeight: 700, textAlign: 'right', paddingBottom: 2 }}>{gross} gm</td>
              </tr>
              <tr>
                <td style={{ color: '#8c8c8c', paddingBottom: 2 }}>Net Weight</td>
                <td style={{ fontWeight: 700, textAlign: 'right', color: '#389e0d', paddingBottom: 2 }}>{net} gm</td>
              </tr>

              {isWalkIn ? (
                <tr>
                  <td style={{ color: '#8c8c8c', paddingBottom: 2 }}>Purchase Value</td>
                  <td style={{ fontWeight: 700, textAlign: 'right', color: '#096dd9', paddingBottom: 2 }}>
                    {fmt(caseData.finalPaymentToCustomer)}
                  </td>
                </tr>
              ) : (
                <>
                  <tr>
                    <td style={{ color: '#8c8c8c', paddingBottom: 2 }}>Closure Amount</td>
                    <td style={{ fontWeight: 700, textAlign: 'right', paddingBottom: 2 }}>
                      {fmt(caseData.closureAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#8c8c8c', paddingBottom: 2 }}>Final Net Value</td>
                    <td style={{ fontWeight: 700, textAlign: 'right', color: '#389e0d', paddingBottom: 2 }}>
                      {fmt(caseData.finalNetValue || goldReceipt?.finalNetValue)}
                    </td>
                  </tr>
                </>
              )}

              <tr>
                <td style={{ color: '#8c8c8c', paddingBottom: 2 }}>Customer</td>
                <td style={{ fontWeight: 600, textAlign: 'right', paddingBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
                  {customer.name || '—'}
                </td>
              </tr>
              <tr>
                <td style={{ color: '#8c8c8c' }}>Branch</td>
                <td style={{ fontWeight: 600, textAlign: 'right' }}>{branch.name || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function handlePrint(caseData, caseModel, goldReceipt) {
  const customer  = caseData.customerId || {};
  const branch    = caseData.branch     || {};
  const isWalkIn  = caseModel === 'WalkInPurchaseCase';
  const weights   = resolveWeights(caseData, caseModel, goldReceipt);
  const gross     = weights.gross.toFixed(4);
  const net       = weights.net.toFixed(4);
  const typeLabel = isWalkIn ? 'WALK-IN PURCHASE' : 'PLEDGE RELEASE';
  const typeColor = isWalkIn ? '#d46b08' : '#531dab';
  const qrContent = buildQrContent(caseData, caseModel, goldReceipt);
  const dateStr   = dayjs().format('DD MMM YYYY, hh:mm A');

  const qrSvg = renderToStaticMarkup(
    <QRCodeSVG value={qrContent} size={130} bgColor="#ffffff" fgColor="#000000" level="M" />
  );

  // Extra rows differ by case type
  const extraRows = isWalkIn
    ? `<tr><td class="muted">Purchase Value</td><td class="blue">${fmt(caseData.finalPaymentToCustomer)}</td></tr>`
    : `<tr><td class="muted">Closure Amount</td><td>${fmt(caseData.closureAmount)}</td></tr>
       <tr><td class="muted">Final Net Value</td><td class="green">${fmt(caseData.finalNetValue || goldReceipt?.finalNetValue)}</td></tr>`;

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Gold Vault Label — ${caseData.caseId}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; background: #fff; }
    .label { width: 9.5cm; border: 2px solid #262626; border-radius: 6px; overflow: hidden; margin: 0.5cm auto; }
    .header { background: ${typeColor}; color: #fff; padding: 6px 12px; display: flex; justify-content: space-between; align-items: center; }
    .header-title { font-weight: 700; font-size: 13pt; letter-spacing: 0.5px; }
    .header-type  { font-size: 10pt; opacity: 0.9; }
    .body  { display: flex; padding: 10px; gap: 10px; }
    .qr-block { display: flex; flex-direction: column; align-items: center; gap: 3px; }
    .qr-note  { font-size: 8pt; color: #595959; }
    .detail   { flex: 1; }
    .case-id  { font-size: 17pt; font-weight: 700; color: #262626; line-height: 1.1; }
    .date     { font-size: 9pt; color: #595959; margin-bottom: 5px; }
    hr { border: none; border-top: 1px solid #d9d9d9; margin: 4px 0; }
    table { width: 100%; font-size: 10.5pt; }
    td { padding-bottom: 3px; }
    td:last-child { text-align: right; font-weight: 700; }
    .muted { color: #8c8c8c; font-weight: normal; }
    .green { color: #389e0d; }
    .blue  { color: #096dd9; }
    .red   { color: #cf1322; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="label">
    <div class="header">
      <span class="header-title">GOLD VAULT LABEL</span>
      <span class="header-type">${typeLabel}</span>
    </div>
    <div class="body">
      <div class="qr-block">
        ${qrSvg}
        <span class="qr-note">Scan to verify</span>
      </div>
      <div class="detail">
        <div class="case-id">${caseData.caseId}</div>
        <div class="date">${dateStr}</div>
        <hr/>
        <table>
          <tr><td class="muted">Gross Weight</td><td>${gross} gm</td></tr>
          <tr><td class="muted">Net Weight</td><td class="green">${net} gm</td></tr>
          ${extraRows}
          <tr><td class="muted">Customer</td><td>${customer.name || '—'}</td></tr>
          <tr><td class="muted">Branch</td><td>${branch.name || '—'}</td></tr>
        </table>
      </div>
    </div>
  </div>
  <script>window.onload = function(){ window.print(); };</script>
</body>
</html>`;

  const w = window.open('', '_blank', 'width=500,height=480');
  w.document.write(html);
  w.document.close();
}

export default function GoldVaultLabel({ caseData, caseModel, goldReceipt, visible, onClose }) {
  if (!caseData) return null;
  return (
    <Modal
      title={
        <Space>
          <SafetyCertificateOutlined style={{ color: '#52c41a' }} />
          Gold Vault Label
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <Space>
          <Button type="primary" icon={<PrinterOutlined />} onClick={() => handlePrint(caseData, caseModel, goldReceipt)}>
            Print Label
          </Button>
          <Button icon={<CloseOutlined />} onClick={onClose}>Close</Button>
        </Space>
      }
      width={440}
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Attach this label to the sealed gold packet before placing it in the physical vault.
        </Text>
        <LabelCard caseData={caseData} caseModel={caseModel} goldReceipt={goldReceipt} />
        <Text type="secondary" style={{ fontSize: 11 }}>
          QR code encodes case ID, weights, financials, customer, and branch.
        </Text>
      </div>
    </Modal>
  );
}
